/**
 * @file statementIntegrity.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-01
 * @modified 2026-11-03
 *
 * Pure helpers for the statement sync and the statement integrity check:
 * how the approval table is ordered (mismatches first), what each status is
 * called, and the small text formatters the sync panel, the badge and the
 * integrity modal share. Nothing here fetches or touches the DOM, so all of it
 * is unit-tested in statementIntegrity.test.js.
 *
 * The server decides every verdict. The client only orders and words them.
 *
 * 1.1.0 (19 Oct 2026): a statement right as at its own period end that differs
 * from Xero now only through credits allocated after it is ADVISORY on the
 * server (`changedSince`) — amber "Changed since", never pinned red; the words
 * say regenerating is optional (changedSinceOf / changedSinceNote).
 *
 * 1.2.0 (3 Nov 2026): a statement whose only difference with Xero is invoices
 * approved here, not yet in Xero (held — the server's `heldHere`) is ADVISORY
 * too: amber "Held here", never pinned red (heldHereOf).
 */

import { formatCurrency } from './currencyFormatter';

/** A check row still `pending` this long after it was written has no live job behind it. */
export const PENDING_STALE_MS = 15 * 60 * 1000;

/** useJobStream statuses that mean "a job is still in flight". */
export const LIVE_JOB_STATUSES = ['starting', 'connecting', 'running', 'reconnecting'];
export const isLiveJob = (status) => LIVE_JOB_STATUSES.includes(status);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ─── Status and ordering ────────────────────────────────────────────────────

/**
 * The status a row should be SHOWN with. Identical to the stored one except a
 * `pending` row whose check started more than 15 minutes ago: the run check has
 * a 5-minute deadline, so nothing is still working on it — it was interrupted.
 */
export function effectiveIntegrityStatus(integrity, now = Date.now()) {
  if (!integrity || !integrity.status) return 'unchecked';
  if (integrity.status === 'pending') {
    const t = Date.parse(integrity.checkedAt);
    if (Number.isFinite(t) && now - t > PENDING_STALE_MS) return 'interrupted';
  }
  return integrity.status;
}

export function isUnacknowledgedMismatch(row) {
  const i = row?.integrity;
  return !!i && i.status === 'mismatch' && !i.acknowledged;
}

/**
 * Where a statement sits in the approval table, before the user's own sort:
 *   0 unacknowledged mismatch · 1 error / interrupted · 2 acknowledged mismatch
 *   3 advisory · 4 everything else (match, pending, unchecked)
 */
export function integrityRank(row, now = Date.now()) {
  const i = row?.integrity;
  const s = effectiveIntegrityStatus(i, now);
  if (s === 'mismatch') return i.acknowledged ? 2 : 0;
  if (s === 'error' || s === 'interrupted') return 1;
  if (s === 'advisory') return 3;
  return 4;
}

const isBlank = (v) => v === null || v === undefined || v === '';

function compareValues(a, b) {
  if (isBlank(a) && isBlank(b)) return 0;
  if (isBlank(a)) return -1;
  if (isBlank(b)) return 1;
  const na = typeof a === 'number' ? a : (typeof a === 'string' && a.trim() !== '' && !Number.isNaN(Number(a)) ? Number(a) : null);
  const nb = typeof b === 'number' ? b : (typeof b === 'string' && b.trim() !== '' && !Number.isNaN(Number(b)) ? Number(b) : null);
  if (na !== null && nb !== null) return na === nb ? 0 : (na < nb ? -1 : 1);
  return String(a).localeCompare(String(b), 'en-GB', { numeric: true, sensitivity: 'base' });
}

/**
 * A NEW array: integrity rank first (mismatches always on top, whichever way
 * the user sorts), then the user's column in their direction, then account
 * number, then the incoming order. Never mutates `rows` — the old in-place sort
 * reordered React state behind its back.
 */
export function sortStatements(rows, field, dir = 'asc', now = Date.now()) {
  const d = dir === 'desc' ? -1 : 1;
  const sortable = field && field !== 'integrity';
  return (rows || [])
    .map((row, idx) => ({ row, idx, rank: integrityRank(row, now) }))
    .sort((a, b) =>
      (a.rank - b.rank)
      || (sortable ? d * compareValues(a.row[field], b.row[field]) : 0)
      || compareValues(a.row.accountNo, b.row.accountNo)
      || (a.idx - b.idx))
    .map((x) => x.row);
}

/** Counts for the step-2 summary line, from the rows actually on screen. */
export function summariseIntegrity(rows, now = Date.now()) {
  const out = {
    total: 0, checked: 0, match: 0, advisory: 0, mismatch: 0, mismatchUnacknowledged: 0,
    error: 0, pending: 0, unchecked: 0, staleSinceSync: 0, lastCheckedAt: null
  };
  let last = null;
  for (const row of rows || []) {
    out.total += 1;
    const i = row?.integrity;
    const s = effectiveIntegrityStatus(i, now);
    if (s === 'match') out.match += 1;
    else if (s === 'advisory') out.advisory += 1;
    else if (s === 'mismatch') { out.mismatch += 1; if (!i.acknowledged) out.mismatchUnacknowledged += 1; }
    else if (s === 'error' || s === 'interrupted') out.error += 1;
    else if (s === 'pending') out.pending += 1;
    else out.unchecked += 1;
    if (['match', 'advisory', 'mismatch'].includes(s)) out.checked += 1;
    if (i?.staleSinceSync) out.staleSinceSync += 1;
    const t = i?.checkedAt ? Date.parse(i.checkedAt) : NaN;
    if (Number.isFinite(t) && (last === null || t > last)) last = t;
  }
  out.lastCheckedAt = last === null ? null : new Date(last).toISOString();
  return out;
}

/** The selection a sync result belongs to: dates plus the sorted contact ids. */
export function selectionKeyFor(dates, contacts) {
  const ids = (contacts || [])
    .map((c) => Number(c && typeof c === 'object' ? c.id : c))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  return `${dates?.start || ''}|${dates?.end || ''}|${ids.join(',')}`;
}

// ─── Right as at the period end, changed since ──────────────────────────────

/**
 * The server's `changedSince` for a check (list row or modal `latest`), or
 * null: the statement was right as at its period end and differs from Xero now
 * only through credits allocated after it. Only ever on an advisory.
 */
export function changedSinceOf(integrity) {
  if (!integrity || effectiveIntegrityStatus(integrity) !== 'advisory') return null;
  const c = integrity.changedSince || integrity.detail?.changedSince || null;
  if (c) return c;
  const r = (integrity.reasons || []).find((x) => x?.code === 'X_CHANGED_SINCE_PE');
  return r ? { periodEnd: null, allocations: r.allocations || [], message: r.message } : null;
}

export const CHANGED_SINCE_LABEL = 'Changed since';

/**
 * The server's `heldHere` for a check, or null: the statement includes
 * invoices approved here and not yet in Xero, and they are all of its
 * difference with Xero. Only ever on an advisory. { docs: [{ docNo, amount,
 * reason }], total }
 */
export function heldHereOf(integrity) {
  if (!integrity || effectiveIntegrityStatus(integrity) !== 'advisory') return null;
  const h = integrity.heldHere || integrity.detail?.heldHere || null;
  if (h) return h;
  const rows = (integrity.reasons || []).filter((x) => x?.code === 'X_HELD_HERE');
  return rows.length ? { docs: rows.map((r) => ({ docNo: r.docNo, amount: (Number(r.diff) || 0) / 100, reason: null })), total: null } : null;
}

export const HELD_HERE_LABEL = 'Held here';

/** '2026-09-30' → '30/09/2026'. */
const dmyOf = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

/**
 * The line the check window shows for a statement right as at its date:
 * "Right as at 30/09/2026 — the customer is told the right figure for that
 * date. Since then: CN-1771 (£310.00) allocated to INV-15840. Regenerating is
 * optional — it shows the latest (£45,700.48 in Xero now)."
 */
export function changedSinceNote(c) {
  if (!c) return '';
  const end = dmyOf(c.periodEnd);
  const allocs = (c.allocations || []).slice(0, 3).map((a) => {
    const amt = Number.isFinite(Number(a.amount)) && Number(a.amount) ? ` (${formatCurrency(Number(a.amount) / 100)})` : '';
    if (a.counterpart && a.docNo) return `${a.counterpart}${amt} allocated to ${a.docNo}`;
    return `${a.counterpart || a.docNo || 'a credit'}${amt}`;
  });
  const more = (c.allocations || []).length > 3 ? ` and ${(c.allocations || []).length - 3} more` : '';
  const since = allocs.length ? `Since then: ${allocs.join('; ')}${more}.` : 'Since then, credits were allocated after the period end.';
  const latest = c.xeroNow !== undefined && c.xeroNow !== null ? ` (${formatCurrency(c.xeroNow)} in Xero now)` : '';
  return `Right as at ${end || 'the period end'} — the customer is told the right figure for that date. ${since} `
    + `Regenerating is optional — it shows the latest${latest}.`;
}

// ─── Labels ─────────────────────────────────────────────────────────────────

export const INTEGRITY_STATUS_LABELS = {
  match: 'Matches Xero',
  advisory: 'Note',
  mismatch: 'Mismatch',
  error: 'Not fully checked',
  interrupted: 'Check interrupted',
  pending: 'Checking…',
  unchecked: 'Not checked'
};

export const REASON_SEVERITY_ORDER = ['mismatch', 'error', 'advisory', 'info'];
export const REASON_SEVERITY_LABELS = {
  mismatch: 'Needs attention — the customer would be told the wrong amount',
  error: 'Could not be fully checked',
  advisory: 'Notes — the closing figure is right',
  info: 'Pennies and other information'
};

export const EXCLUDED_REASON_TEXT = {
  unchecked: 'Not yet approved for statements',
  unapproved: 'Marked not for statements',
  penny: '1p or less — left off by the statement’s rules'
};

export function coverageReasonText(reason, periodEndLabel) {
  switch (reason) {
    case 'no_statement_flag': return 'The account’s “Statement” tick is off';
    case 'linked_child_parent_not_selected': return 'Linked to a parent account that was not selected';
    case 'prefilter_current_status':
      return `Owed at ${periodEndLabel || 'the period end'}, but nothing on it is unpaid and approved now, so generation skipped it`;
    case 'generator_skipped': return 'Generation found nothing to put on a statement';
    default: return reason || 'No statement';
  }
}

/** Names for the Xero list reads in the receivables snapshot (§4.3). */
const SNAPSHOT_LIST_LABELS = {
  openInvoices: 'open invoices',
  changedInvoices: 'invoices changed since the period end',
  creditNotes: 'credit notes',
  overpayments: 'overpayments',
  prepayments: 'prepayments',
  paymentsAfterPE: 'payments dated after the period end',
  followUps: 'follow-up documents'
};
export const snapshotListLabel = (list) => SNAPSHOT_LIST_LABELS[list] || list || 'a list';

/** Entity type for EntityLink from a difference's or document's docType. */
export function docEntityType(docType) {
  switch (String(docType || '').toLowerCase()) {
    case 'invoice': return 'invoice';
    case 'credit':
    case 'credit_note':
    case 'creditnote': return 'credit_note';
    // Overpayments and prepayments are invoicing.invoices rows too.
    case 'overpayment':
    case 'prepayment': return 'invoice';
    case 'payment': return 'payment';
    default: return null;
  }
}

/** The message carried by a problem, whichever shape the server sent. */
export function problemText(p) {
  if (!p) return '';
  if (typeof p === 'string') return p;
  return p.message || p.error || p.reason || p.code || JSON.stringify(p);
}

// ─── Formatting ─────────────────────────────────────────────────────────────

export const money = (v) => (v === null || v === undefined || v === '' ? '—' : formatCurrency(v));

/** '2026-09-30' → '30 Sep 2026' (a date column, so no time-zone arithmetic). */
export function formatYmdLong(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return '';
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/** '2026-09-30' → '30 Sep'. */
export function formatYmdShort(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return '';
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`;
}

function londonParts(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'numeric',
    year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).formatToParts(d);
  const get = (t) => (parts.find((p) => p.type === t) || {}).value;
  return {
    weekday: get('weekday'), day: Number(get('day')), month: Number(get('month')),
    year: get('year'), hour: get('hour'), minute: get('minute')
  };
}

/** An instant as the office reads it: 'Tue 30 Sep, 03:13' (Europe/London). */
export function formatLondonWhen(value) {
  const p = londonParts(value);
  if (!p) return '';
  return `${p.weekday} ${p.day} ${MONTHS[p.month - 1]}, ${p.hour}:${p.minute}`;
}

/** '30 Sep 2026, 15:12' (Europe/London). */
export function formatLondonDateTime(value) {
  const p = londonParts(value);
  if (!p) return '';
  return `${p.day} ${MONTHS[p.month - 1]} ${p.year}, ${p.hour}:${p.minute}`;
}

/** '14:51' (Europe/London). */
export function formatLondonClock(value) {
  const p = londonParts(value);
  return p ? `${p.hour}:${p.minute}` : '';
}

/** 38000 → '38 s'; 125000 → '2 min 5 s'. */
export function formatDuration(ms) {
  if (!Number.isFinite(ms) || ms < 0) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m} min ${r} s` : `${m} min`;
}

export const plural = (n, one, many) => `${n} ${n === 1 ? one : (many || `${one}s`)}`;

const countOf = (v) => {
  if (Array.isArray(v)) return v.length;
  if (v && typeof v === 'object' && Number.isFinite(v.count)) return v.count;
  return Number.isFinite(v) ? v : 0;
};

/** The words for each count key a sync step reports (§1.3). */
const COUNT_WORDS = {
  fetched: 'fetched', added: 'added', updated: 'updated', unchanged: 'unchanged',
  figureHealed: 'figures corrected', errors: 'errors', skippedNoAccount: 'skipped (no account here)',
  tombstonesIgnored: 'deleted in Xero, ignored', creditDocs: 'credit documents', matchedLocally: 'matched here',
  imported: 'imported', reconciled: 'reconciled', skippedTargetNotLocal: 'skipped (invoice not held here)',
  fetchedSeparately: 'fetched separately', invoicesLinked: 'invoices linked', billsLinked: 'bills linked',
  xeroCalls: 'Xero calls', checked: 'checked', identical: 'identical', differences: 'differences',
  explained: 'explained'
};

/** Short counts for one step: '41 fetched · 41 updated · 0 errors' (zeros left out, errors always shown when present). */
export function formatStepCounts(key, counts) {
  if (!counts || typeof counts !== 'object') return '';
  const out = [];
  for (const [k, word] of Object.entries(COUNT_WORDS)) {
    if (!(k in counts)) continue;
    const n = countOf(counts[k]);
    if (n > 0) out.push(`${n} ${word}`);
  }
  if (key === 'documents' && counts.byType && typeof counts.byType === 'object') {
    const t = counts.byType;
    const bits = [
      t.invoices ? plural(t.invoices, 'invoice') : null,
      t.credits ? plural(t.credits, 'credit note') : null,
      t.overpayments ? plural(t.overpayments, 'overpayment') : null,
      t.prepayments ? plural(t.prepayments, 'prepayment') : null
    ].filter(Boolean);
    if (bits.length) out.push(`(${bits.join(', ')})`);
  }
  return out.join(' · ');
}

/** How many differences a reconciliation summary found, including any beyond the 500 stored. */
export function differenceCountOf(summary) {
  if (!summary) return 0;
  if (Number.isFinite(summary.differenceCount)) return summary.differenceCount;
  if (Number.isFinite(summary.totals?.differences)) return summary.totals.differences;
  return Array.isArray(summary.differences) ? summary.differences.length : 0;
}

/** Problems a summary carries, in words: the server's own list plus failed snapshot reads. */
export function summaryProblems(summary) {
  if (!summary) return [];
  const out = (summary.problems || []).map(problemText).filter(Boolean);
  for (const f of summary.snapshot?.failures || []) {
    out.push(`Couldn’t read Xero’s ${snapshotListLabel(f.list)} — this comparison is incomplete${f.message ? ` (${f.message})` : ''}`);
  }
  return out;
}

/** The sync panel's headline. */
export function syncHeadline(summary, resultStatus) {
  const n = differenceCountOf(summary);
  if (n > 0) return `${n} ${n === 1 ? 'difference needs' : 'differences need'} attention`;
  if (resultStatus === 'completed_with_errors' || summary?.status === 'incomplete' || summaryProblems(summary).length > 0) {
    return 'Sync finished with problems';
  }
  if (summary?.status === 'identical') return 'Everything matches Xero';
  return summary ? 'Everything matches Xero' : 'Sync finished';
}

/**
 * A persisted statement_sync_runs row (GET /api/statements/sync/:id) as the
 * same shape a job's `complete.result` has, so the panel renders either. The
 * route returns the row; tolerate camelCase or snake_case and a wrapper.
 */
export function normalisePersistedSync(data) {
  if (!data || data.available === false) return null;
  const row = data.run || data.row || data.syncRun || data;
  if (!row || (row.id === undefined && row.syncRunId === undefined && !row.reconciliation)) return null;
  const pick = (...keys) => { for (const k of keys) if (row[k] !== undefined) return row[k]; return undefined; };
  const startedAt = pick('startedAt', 'started_at');
  const finishedAt = pick('finishedAt', 'finished_at');
  const status = pick('status') || 'completed';
  const durationMs = startedAt && finishedAt ? Date.parse(finishedAt) - Date.parse(startedAt) : null;
  if (status === 'running' || status === 'abandoned' || status === 'failed') {
    return {
      kind: 'statement-sync', done: true, failed: true,
      status: status === 'running' ? 'abandoned' : status,
      error: pick('error') || (status === 'failed' ? 'The sync failed' : 'The server restarted during the run'),
      syncRunId: pick('id', 'syncRunId'), persisted: true, steps: pick('steps') || [],
      summary: pick('reconciliation') || null, startedAt, finishedAt, durationMs
    };
  }
  return {
    kind: 'statement-sync', done: true, status,
    syncRunId: pick('id', 'syncRunId'), persisted: true,
    steps: pick('steps') || [], summary: pick('reconciliation') || null,
    startedAt, finishedAt, durationMs,
    startedBy: pick('startedBy') || null
  };
}

/**
 * One integrity check (the modal's `latest`, or a history entry) with its
 * figures under one set of names, whichever spelling the route used.
 */
export function normaliseCheck(c) {
  if (!c) return null;
  const num = (...vals) => {
    for (const v of vals) if (v !== undefined && v !== null && v !== '') return Number(v);
    return null;
  };
  const f = c.figures || {};
  return {
    ...c,
    headline: c.headline || c.detail?.headline || null,
    changedSince: c.changedSince || c.detail?.changedSince || null,
    heldHere: c.heldHere || c.detail?.heldHere || null,
    checkedAt: c.checkedAt || c.checked_at || null,
    reportConfirmed: c.reportConfirmed !== undefined ? c.reportConfirmed : (c.report_confirmed !== undefined ? c.report_confirmed : null),
    figures: {
      stored: num(f.stored, c.stored, c.storedClosing, c.stored_closing),
      local: num(f.local, c.local, c.localClosing, c.local_closing),
      xero: num(f.xero, c.xero, c.xeroClosing, c.xero_closing),
      xeroAsAt: num(f.xeroAsAt, c.xeroAsAt, c.xeroAsatClosing, c.xero_asat_closing)
    },
    reasons: Array.isArray(c.reasons) ? c.reasons : [],
    detail: c.detail || null
  };
}

/** Reasons grouped by severity, in display order, empty groups dropped. */
export function groupReasons(reasons) {
  const groups = {};
  for (const r of reasons || []) {
    const sev = REASON_SEVERITY_ORDER.includes(r?.severity) ? r.severity : 'info';
    (groups[sev] = groups[sev] || []).push(r);
  }
  return REASON_SEVERITY_ORDER.filter((s) => groups[s]?.length).map((s) => ({ severity: s, reasons: groups[s] }));
}
